import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { PAY_PATH } from '@/covered';

import { closeOpenDialog, useTelegramBackButton } from '@/platform';

type Handler = () => void;

type Guard = (proceed: () => void) => void;

interface BackState {
  canBack: boolean;
  back: () => void;
  push: (handler: Handler) => () => void;
  go: (to: string) => void;
  setGuard: (guard: Guard | null) => void;
}

const BackContext = createContext<BackState>({
  canBack: false,
  back: () => {},
  push: () => () => {},
  go: (to) => {
    window.location.hash = to;
  },
  setGuard: () => {},
});

const OPEN_DIALOG = '[role$="dialog"][data-state="open"]';

function subscribeDialogs(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined') return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-state'],
  });
  return () => observer.disconnect();
}

const hasOpenDialog = () => document.querySelector(OPEN_DIALOG) !== null;

const typing = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export function BackProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const { pathname } = useLocation();
  const stack = useRef<Handler[]>([]);
  const guard = useRef<Guard | null>(null);
  const setGuard = useCallback((g: Guard | null) => {
    guard.current = g;
  }, []);
  const guarded = useCallback((proceed: () => void) => {
    if (guard.current) guard.current(proceed);
    else proceed();
  }, []);
  const go = useCallback((to: string) => guarded(() => navigate(to)), [guarded, navigate]);
  const [depth, setDepth] = useState(0);
  const dialogOpen = useSyncExternalStore(subscribeDialogs, hasOpenDialog, () => false);

  const push = useCallback((handler: Handler) => {
    stack.current.push(handler);
    setDepth(stack.current.length);
    return () => {
      stack.current = stack.current.filter((h) => h !== handler);
      setDepth(stack.current.length);
    };
  }, []);

  const back = useCallback(() => {
    if (closeOpenDialog()) return;
    const top = stack.current[stack.current.length - 1];
    if (top) return top();
    if (pathname !== '/') guarded(() => navigate('/'));
  }, [navigate, pathname, guarded]);

  const canBack = dialogOpen || depth > 0 || pathname !== '/';

  useLayoutEffect(() => {
    if (navType === 'POP' || PAY_PATH.test(pathname)) return;
    document.scrollingElement?.scrollTo(0, 0);
  }, [pathname, navType]);

  useTelegramBackButton(canBack, back);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (hasOpenDialog() || typing(document.activeElement)) return;
      back();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [back]);

  const value = useMemo(
    () => ({ canBack, back, push, go, setGuard }),
    [canBack, back, push, go, setGuard],
  );
  return <BackContext.Provider value={value}>{children}</BackContext.Provider>;
}

export function useBack(): Pick<BackState, 'canBack' | 'back'> {
  const { canBack, back } = useContext(BackContext);
  return { canBack, back };
}

export function useGo(): (to: string) => void {
  return useContext(BackContext).go;
}

export function useLeaveGuard(active: boolean, onAttempt: (proceed: () => void) => void): void {
  const { setGuard } = useContext(BackContext);
  const ref = useRef(onAttempt);
  useEffect(() => {
    ref.current = onAttempt;
  });
  useEffect(() => {
    if (!active) return;
    setGuard((proceed) => ref.current(proceed));
    return () => setGuard(null);
  }, [active, setGuard]);
}

export function useBackHandler(active: boolean, handler: Handler): void {
  const { push } = useContext(BackContext);
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!active) return;
    return push(() => ref.current());
  }, [active, push]);
}
